/* Converts the compact mobile order ticket into the shared manual Order Entry payload. */
import {
  Direction,
  OrderType,
  OrderTypes,
  TimeInForce,
  type OrderEntryOrder,
  type OrderRecord,
  type ProductSpecification,
  type TraderDBAccount,
  type TradingAccount,
} from '@qep/types/src';
import {type Product} from '@qep/shared/src/utils/Product';
import {
  type MobileOrderDraft,
  type MobileOrderFieldErrors,
  type MobileOrderSide,
  type MobileOrderType,
  type MobileTimeInForce,
} from './mobile-order-types.js';

/** Parses finite positive user-entered numeric fields without accepting empty or non-numeric strings. */
function parsePositiveNumber(value: string): number | undefined {
  const trimmedValue = value.trim();
  if (!/^\d+(?:\.\d+)?$/.test(trimmedValue)) {
    return undefined;
  }

  const parsed = Number.parseFloat(trimmedValue);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Checks whether a TraderDB product specification can route the selected product. */
function productSpecificationMatchesProduct(specification: ProductSpecification, product: Product): boolean {
  const {rootSymbol, underlyingRootSymbol, segmentMic} = product;
  const acceptableRoots = [rootSymbol, underlyingRootSymbol].filter((root): root is string => typeof root === 'string' && root.length > 0);

  return acceptableRoots.includes(specification.symbol)
    && (specification.exchangeCode === segmentMic || specification.exchangeName === segmentMic);
}

/** Converts a TraderDB account into the account payload shape expected by the shared order service. */
function toTradingAccount(account: TraderDBAccount): TradingAccount {
  return {
    id: account.id,
    accountId: account.neovestAccount,
    accountCode: account.code,
    execCode: account.code,
    description: account.description,
    memo: account.memo ?? '',
    clearingString: account.clearingString ?? '',
    notes: [
      account.tradeIntentions[0]?.tradeIntentionName ?? '',
      account.customText2 ?? '',
      account.customText3 ?? '',
    ],
    tradeIntentionId: account.tradeIntentions[0]?.id,
  };
}

/** Maps the compact buy/sell control to the Direction enum consumed by shared manual order placement. */
function toDirection(side: MobileOrderSide): Direction {
  return side === 'sell' ? Direction.Sell : Direction.Buy;
}

/** Maps the compact type control to the order type enum consumed by the OMS request builder. */
function toOrderType(orderType: MobileOrderType): OrderType {
  return orderType === 'limit' ? OrderType.ORDERTYPE_LIMIT : OrderType.ORDERTYPE_MARKET;
}

/** Maps the compact TIF control to the TimeInForce enum consumed by the OMS request builder. */
function toTimeInForce(timeInForce: MobileTimeInForce): TimeInForce {
  return timeInForce === 'gtc'
    ? TimeInForce.TIMEINFORCE_UNTIL_CANCELED
    : TimeInForce.TIMEINFORCE_REGULAR_TRADING_HOURS;
}

/** Returns accounts that are active, trading-enabled, and tradable for the selected product. */
export function findMobileTradableAccounts(accounts: TraderDBAccount[], product?: Product): TraderDBAccount[] {
  if (!product?.symbolDetails) {
    return [];
  }

  return accounts.filter(account =>
    account.isActive
    && account.action === 'Trading'
    && account.productSpecifications.some(specification => productSpecificationMatchesProduct(specification, product)));
}

/** Validates the compact mobile draft before it can be mapped to an executable order payload. */
export function validateMobileOrderDraft(draft: MobileOrderDraft): MobileOrderFieldErrors {
  const errors: MobileOrderFieldErrors = {};

  if (!draft.product?.symbolDetails) {
    errors.symbol = 'Select a symbol.';
  }

  if (!draft.accountId) {
    errors.account = 'Select an account.';
  }

  if (parsePositiveNumber(draft.quantity) === undefined) {
    errors.quantity = 'Enter a quantity greater than 0.';
  }

  if (draft.orderType === 'limit' && parsePositiveNumber(draft.limitPrice) === undefined) {
    errors.limitPrice = 'Enter a limit price greater than 0.';
  }

  return errors;
}

/** Throws the first validation message so submit handlers can show one actionable failure. */
function assertNoValidationErrors(errors: MobileOrderFieldErrors): void {
  const [message] = Object.values(errors);
  if (message) {
    throw new Error(message);
  }
}

/** Builds the shared manual OrderEntryOrder payload for the real mobile submit path. */
export function buildMobileOrderPayload(draft: MobileOrderDraft, accounts: TraderDBAccount[]): OrderEntryOrder {
  assertNoValidationErrors(validateMobileOrderDraft(draft));

  const product = draft.product!;
  const symbol = product.symbolDetails!;
  const quantity = parsePositiveNumber(draft.quantity)!;
  const account = findMobileTradableAccounts(accounts, product).find(candidate => candidate.id === draft.accountId);

  if (!account) {
    throw new Error('Select an account.');
  }

  const leg: OrderRecord = {
    key: `${symbol.segmentMic}:${symbol.exchangeSymbol}`,
    symbol,
    quantity,
    direction: toDirection(draft.side),
    account: toTradingAccount(account),
  };

  return {
    ticketType: OrderTypes.Manual,
    legs: [leg],
    params: {
      orderType: toOrderType(draft.orderType),
      limitPrice: draft.orderType === 'limit' ? draft.limitPrice : undefined,
      timeInForce: toTimeInForce(draft.timeInForce),
    },
    symbols: [symbol],
  };
}
